/**
 * Zwei kleine Helfer für die Bewegungen auf der öffentlichen Seite.
 *
 * Beide gibt es, weil Animation hier ein Zusatz ist und nie eine Bedingung.
 * Wer „Bewegung reduzieren" eingestellt hat – am iPhone unter Bedienungs-
 * hilfen, am Rechner in den Systemeinstellungen – bekommt dieselbe Seite
 * ohne Fahrt: kein Einblenden, kein selbsttätiges Weiterblättern. Das ist
 * keine Höflichkeit, sondern für Menschen mit vestibulären Beschwerden der
 * Unterschied zwischen benutzbar und übel.
 */

import { useEffect, useRef, useState } from 'react'

/** Hat der Besucher „Bewegung reduzieren" eingestellt? */
export function useSparsameBewegung(): boolean {
  const [sparsam, setzeSparsam] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false,
  )

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const abfrage = window.matchMedia('(prefers-reduced-motion: reduce)')
    const merken = () => setzeSparsam(abfrage.matches)
    abfrage.addEventListener('change', merken)
    return () => abfrage.removeEventListener('change', merken)
  }, [])

  return sparsam
}

/**
 * Blendet einen Abschnitt ein, sobald er ins Bild kommt.
 *
 * Der Rückgabewert ist ein Verweis für das Element. Die Sichtbarkeit steckt
 * in der Klasse `sichtbar`, die CSS setzt – nicht in einem Zustand, der bei
 * jedem Scrollschritt neu rendert.
 *
 * Wichtig ist der Ausfallweg: kennt der Browser den IntersectionObserver
 * nicht, wird sofort eingeblendet. Ein Abschnitt, den man nicht lesen kann,
 * weil eine Beobachtung fehlschlug, wäre der schlechteste Ausgang.
 */
export function useEinblenden<T extends HTMLElement>() {
  const element = useRef<T>(null)

  useEffect(() => {
    const knoten = element.current
    if (!knoten) return

    if (typeof IntersectionObserver !== 'function') {
      knoten.classList.add('sichtbar')
      return
    }

    const beobachter = new IntersectionObserver(
      (eintraege) => {
        for (const eintrag of eintraege) {
          if (!eintrag.isIntersecting) continue
          eintrag.target.classList.add('sichtbar')
          // Einmal eingeblendet bleibt eingeblendet. Ein Abschnitt, der
          // beim Zurückscrollen wieder verschwindet, wirkt kaputt.
          beobachter.unobserve(eintrag.target)
        }
      },
      { rootMargin: '0px 0px -10% 0px' },
    )
    beobachter.observe(knoten)
    return () => beobachter.disconnect()
  }, [])

  return element
}
